// pages/SuperAdminConversationsPage.js
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAuthState } from '../hooks/useAuthState'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import { app } from '../firebase'
import { useCompany } from '../contexts/CompanyContext'
import ChatMessage from '../components/ChatMessage'
import CloseIcon from '@mui/icons-material/Close'
import './SuperAdminConversationsPage.css'
import ChatIcon from '@mui/icons-material/Chat'

function SuperAdminConversationsPage () {
  const { user } = useAuth()
  const { companyId } = useAuthState()
  const { botName } = useCompany()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [conversationMessages, setConversationMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)

  const db = getFirestore(app)

  useEffect(() => {
    if (!user || !companyId) {
      setLoading(false)
      if (!user) {
        setError('Bitte melden Sie sich an')
      } else if (!companyId) {
        setError('Keine Company zugeordnet')
      }
      return
    }

    const loadConversations = async () => {
      try {
        setLoading(true)
        setError(null)

        // Load conversations from company
        const conversationsRef = collection(
          db,
          'companies',
          companyId,
          'conversations'
        )
        const conversationsQuery = query(
          conversationsRef,
          orderBy('lastActivity', 'desc'),
          limit(50)
        )

        const conversationsSnapshot = await getDocs(conversationsQuery)
        const conversationsData = conversationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          lastActivity: doc.data().lastActivity?.toDate?.() || doc.data().lastActivity,
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
          lastMessage: doc.data().lastMessage
            ? {
                ...doc.data().lastMessage,
                timestamp:
                  doc.data().lastMessage.timestamp?.toDate?.() ||
                  doc.data().lastMessage.timestamp
              }
            : null
        }))

        setConversations(conversationsData)
        setLoading(false)
      } catch (err) {
        console.error('Error loading conversations:', err)
        setError('Fehler beim Laden der Konversationen: ' + err.message)
        setLoading(false)
      }
    }

    loadConversations()
  }, [db, user, companyId])

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate?.() || new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const truncateText = (text, maxLength = 100) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const loadConversationDetails = async conversationId => {
    if (!companyId) return

    try {
      setLoadingMessages(true)
      setError(null)

      // Load conversation document
      const conversationDocRef = doc(
        db,
        'companies',
        companyId,
        'conversations',
        conversationId
      )
      const conversationDoc = await getDoc(conversationDocRef)

      if (!conversationDoc.exists()) {
        throw new Error('Konversation nicht gefunden')
      }

      const conversationData = {
        id: conversationDoc.id,
        ...conversationDoc.data(),
        createdAt:
          conversationDoc.data().createdAt?.toDate?.() ||
          conversationDoc.data().createdAt,
        updatedAt:
          conversationDoc.data().updatedAt?.toDate?.() ||
          conversationDoc.data().updatedAt,
        lastActivity:
          conversationDoc.data().lastActivity?.toDate?.() ||
          conversationDoc.data().lastActivity
      }

      // Load all messages (not just 50)
      const messagesRef = collection(
        db,
        'companies',
        companyId,
        'conversations',
        conversationId,
        'messages'
      )
      const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'))

      const messagesSnapshot = await getDocs(messagesQuery)

      const messages = messagesSnapshot.docs.map(doc => {
        const messageData = { id: doc.id, ...doc.data() }

        // Convert timestamp
        if (
          messageData.timestamp &&
          typeof messageData.timestamp.toDate === 'function'
        ) {
          messageData.timestamp = messageData.timestamp.toDate()
        }

        return messageData
      })

      setSelectedConversation(conversationData)
      setConversationMessages(messages)
    } catch (err) {
      console.error('Error loading conversation details:', err)
      setError('Fehler beim Laden der Konversationsdetails: ' + err.message)
    } finally {
      setLoadingMessages(false)
    }
  }

  const handleConversationClick = conversation => {
    loadConversationDetails(conversation.id)
  }

  const handleCloseModal = () => {
    setSelectedConversation(null)
    setConversationMessages([])
  }

  // Calculate statistics from conversations
  const statistics = useMemo(() => {
    if (!conversations || conversations.length === 0) {
      return {
        total: 0,
        avgMessages: 0,
        byDevice: [],
        byDate: { today: 0, thisWeek: 0, thisMonth: 0, older: 0 },
        byResponseSize: { small: 0, large: 0 }
      }
    }

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)
    const monthAgo = new Date(today)
    monthAgo.setMonth(monthAgo.getMonth() - 1)

    // Device statistics
    const deviceMap = {}
    conversations.forEach(conv => {
      const device = conv.deviceName || 'Unbekannt'
      deviceMap[device] = (deviceMap[device] || 0) + 1
    })
    const byDevice = Object.entries(deviceMap)
      .map(([device, count]) => ({ device, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    // Date statistics
    const byDate = { today: 0, thisWeek: 0, thisMonth: 0, older: 0 }
    conversations.forEach(conv => {
      const lastActivity = conv.lastActivity
        ? conv.lastActivity instanceof Date
          ? conv.lastActivity
          : conv.lastActivity.toDate?.() || new Date(conv.lastActivity)
        : null

      if (!lastActivity) {
        byDate.older++
        return
      }

      if (lastActivity >= today) {
        byDate.today++
      } else if (lastActivity >= weekAgo) {
        byDate.thisWeek++
      } else if (lastActivity >= monthAgo) {
        byDate.thisMonth++
      } else {
        byDate.older++
      }
    })

    // Response size statistics (small = <= 2 messages, large = > 2 messages)
    const byResponseSize = { small: 0, large: 0 }
    let totalMessages = 0
    conversations.forEach(conv => {
      const msgCount = conv.messageCount || 0
      totalMessages += msgCount
      if (msgCount <= 2) {
        byResponseSize.small++
      } else {
        byResponseSize.large++
      }
    })

    return {
      total: conversations.length,
      avgMessages: totalMessages / conversations.length,
      byDevice,
      byDate,
      byResponseSize
    }
  }, [conversations])

  if (loading) {
    return (
      <div className='superadmin-conversations'>
        <div className='loading'>Lädt Konversationen...</div>
      </div>
    )
  }

  return (
    <div className='superadmin-conversations'>
      <div className='page-header'>
        <div>
          <h1>
            <ChatIcon style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Konversationen
          </h1>
          <p className='page-subtitle'>
            Neueste 50 Konversationen aus Ihrer Company
          </p>
        </div>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Statistics Dashboard */}
      {conversations.length > 0 && (
        <div className='statistics-dashboard'>
          {/* Overview Cards */}
          <div className='stats-overview'>
            <div className='stat-card'>
              <div className='stat-label'>Gesamt</div>
              <div className='stat-value'>{statistics.total}</div>
              <div className='stat-sublabel'>Konversationen</div>
            </div>
            <div className='stat-card'>
              <div className='stat-label'>Durchschnitt</div>
              <div className='stat-value'>
                {statistics.avgMessages.toFixed(1)}
              </div>
              <div className='stat-sublabel'>Nachrichten/Konversation</div>
            </div>
            <div className='stat-card'>
              <div className='stat-label'>Kurze Antworten</div>
              <div className='stat-value stat-value-small'>
                {statistics.byResponseSize.small}
              </div>
              <div className='stat-sublabel'>≤ 2 Nachrichten</div>
            </div>
            <div className='stat-card'>
              <div className='stat-label'>Lange Antworten</div>
              <div className='stat-value stat-value-large'>
                {statistics.byResponseSize.large}
              </div>
              <div className='stat-sublabel'>> 2 Nachrichten</div>
            </div>
          </div>

          {/* Device Distribution */}
          {statistics.byDevice.length > 0 && (
            <div className='stats-section'>
              <h3 className='stats-section-title'>Top Geräte</h3>
              <div className='device-distribution'>
                {statistics.byDevice.map(({ device, count }, index) => {
                  const percentage = (count / statistics.total) * 100
                  return (
                    <div key={device} className='device-item'>
                      <div className='device-header'>
                        <span className='device-rank'>#{index + 1}</span>
                        <span className='device-name'>{device}</span>
                        <span className='device-count'>{count}</span>
                      </div>
                      <div className='device-bar'>
                        <div
                          className='device-bar-fill'
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Date Distribution */}
          <div className='stats-section'>
            <h3 className='stats-section-title'>Verteilung nach Zeitraum</h3>
            <div className='date-distribution'>
              <div className='date-item'>
                <div className='date-label'>Heute</div>
                <div className='date-value'>{statistics.byDate.today}</div>
              </div>
              <div className='date-item'>
                <div className='date-label'>Diese Woche</div>
                <div className='date-value'>{statistics.byDate.thisWeek}</div>
              </div>
              <div className='date-item'>
                <div className='date-label'>Dieser Monat</div>
                <div className='date-value'>{statistics.byDate.thisMonth}</div>
              </div>
              <div className='date-item'>
                <div className='date-label'>Älter</div>
                <div className='date-value'>{statistics.byDate.older}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {conversations.length === 0 ? (
        <div className='empty-state'>
          <p>Keine Konversationen gefunden.</p>
        </div>
      ) : (
        <div className='conversations-table-container'>
          <table className='conversations-table'>
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Titel</th>
                <th>Letzte Nachricht</th>
                <th>Nachrichten</th>
                <th>Letzte Aktivität</th>
              </tr>
            </thead>
            <tbody>
              {conversations.map(conversation => (
                <tr
                  key={conversation.id}
                  className='conversation-row'
                  onClick={() => handleConversationClick(conversation)}
                >
                  <td className='device-cell'>
                    {conversation.deviceName || 'N/A'}
                  </td>
                  <td className='title-cell'>
                    {conversation.title || 'Kein Titel'}
                  </td>
                  <td className='message-cell'>
                    {conversation.lastMessage?.content
                      ? truncateText(conversation.lastMessage.content, 80)
                      : 'Keine Nachricht'}
                  </td>
                  <td className='count-cell'>
                    {conversation.messageCount || 0}
                  </td>
                  <td className='date-cell'>
                    {formatDate(conversation.lastActivity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div className='modal-overlay' onClick={handleCloseModal}>
          <div
            className='modal-content conversation-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <div>
                <h2>
                  {selectedConversation.title || 'Konversation'}
                </h2>
                <div className='modal-subtitle'>
                  {selectedConversation.deviceName && (
                    <span>
                      <strong>Gerät:</strong> {selectedConversation.deviceName}
                    </span>
                  )}
                  <span>
                    <strong>Nachrichten:</strong> {conversationMessages.length}
                  </span>
                </div>
              </div>
              <button className='close-btn' onClick={handleCloseModal}>
                <CloseIcon />
              </button>
            </div>

            <div className='modal-body conversation-messages'>
              {loadingMessages ? (
                <div className='loading-messages'>Lädt Nachrichten...</div>
              ) : conversationMessages.length === 0 ? (
                <div className='empty-messages'>
                  Keine Nachrichten in dieser Konversation.
                </div>
              ) : (
                <div className='messages-container'>
                  {conversationMessages.map((message, index) => (
                    <ChatMessage
                      key={message.id || index}
                      message={message}
                      conversationId={selectedConversation.id}
                      messageIndex={index}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SuperAdminConversationsPage

